import { PlatformSDKShape } from '../PlatformSDKShape';
import { BaseSDKShape } from '../BaseSDKShape';
import { ApplicationContext } from '@wix/editor-platform-contexts';
import type {
  ElementData,
  ElementDataInput,
  ElementDataDefinitions,
  ElementLink,
  ElementSort,
  ElementFilter,
  ElementStyles,
  ElementStylesUpdate,
  ElementStylesRemoval,
  ElementStyleDefinitions,
  ElementPresetDefinitions,
  ElementStateDefinitions,
  BackgroundPickerOptions,
  IElementSDK,
  IFontFamily,
  IFontWeight,
  IFontFamilyAndWeight,
  ISelectLinkSharedOptions,
  IPanelOptions,
  SelectFontParams,
  SelectFontFamilyParams,
  SelectFontWeightParams,
  SelectMediaParams,
  SelectLinkParams,
  SelectSortParams,
  SelectFilterParams,
  SelectColorParams,
  SelectTextThemeParams,
} from '@wix/public-editor-platform-interfaces';

/**
 * Methods for interacting with a component from a custom panel.
 *
 * Import `reactElements` from `@wix/editor` to read and update styles, data,
 * presets, and states, and to open editor pickers.
 *
 * @example
 * ```tsx
 * import { reactElements } from '@wix/editor';
 *
 * export default function MyPanel() {
 *   const handleClick = async () => {
 *     const data = await reactElements.getData();
 *     await reactElements.setData({ title: 'Updated!' });
 *   };
 *   return <button onClick={handleClick}>Update Title</button>;
 * }
 * ```
 */
export class ElementSDKShape extends BaseSDKShape implements IElementSDK {
  constructor(
    protected overriddenApplicationContext: Promise<ApplicationContext> | null = null,
    private childPath: number[] = [],
  ) {
    super(overriddenApplicationContext);
  }

  /**
   * Retrieves the style definitions declared in the component's manifest,
   * including `cssProperties` and `cssCustomProperties`.
   *
   * @returns Style definitions for the component.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const defs = await reactElements.getStyleDefinitions();
   * ```
   */
  async getStyleDefinitions(): Promise<ElementStyleDefinitions> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getStyleDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * Retrieves the current style values for the component.
   *
   * @returns Current `cssProperties` and `cssCustomProperties` values.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const styles = await reactElements.getStyles();
   * const bgColor = styles.cssProperties.backgroundColor;
   * ```
   */
  async getStyles(): Promise<ElementStyles> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getStyles(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * Sets style values for the component.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   * import { CssPropertyType } from '@wix/public-editor-platform-sdk';
   *
   * // Using enum (recommended):
   * await reactElements.setStyles({
   *   cssProperties: { [CssPropertyType.color]: '#fff' }
   * });
   *
   * // Using string literal (also works):
   * await reactElements.setStyles({
   *   cssProperties: { 'color': '#fff' }
   * });
   * ```
   */
  async setStyles(values: ElementStylesUpdate = {}): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.setStyles(
      manifestContextId,
      this.childPath,
      values,
    );
  }

  /**
   * Removes style values from the component.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   * import { CssPropertyType } from '@wix/public-editor-platform-sdk';
   *
   * // Using enum (recommended):
   * await reactElements.removeStyles({
   *   cssPropertiesKeys: [CssPropertyType.color]
   * });
   *
   * // Using string literal (also works):
   * await reactElements.removeStyles({
   *   cssPropertiesKeys: ['color']
   * });
   * ```
   */
  async removeStyles(values: ElementStylesRemoval = {}): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.removeStyles(
      manifestContextId,
      this.childPath,
      values,
    );
  }

  /**
   * Lists the data fields defined on the selected component's manifest.
   *
   * Each key in the result is a valid **`dataItemKey`** for `getData`, `setData`, and `getResolvedData`.
   *
   * @returns Definitions keyed by `dataItemKey`.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const defs = await reactElements.getDataDefinitions();
   * ```
   */
  async getDataDefinitions(): Promise<ElementDataDefinitions> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getDataDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * Retrieves the current data values for the component.
   *
   * @typeParam T - Optional type parameter to define the expected structure of the returned data.
   * @returns Current data values for the component.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const data = await reactElements.getData();
   * ```
   */
  async getData<T extends ElementData = ElementData>(): Promise<T> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getData(
      manifestContextId,
      this.childPath,
    ) as Promise<T>;
  }

  /**
   * Returns one data field from the selected component by **`dataItemKey`**.
   *
   * A **`dataItemKey`** is the property name of a data field in that component's manifest — the same names
   * returned from `getDataDefinitions`. Example: `'heroImage'` for a field declared under `editorElement.data`.
   *
   * `getData` returns all fields at once. This method returns only the field you name.
   *
   * @param options.dataItemKey - Key of the data field to read. Must exist on the component manifest.
   * @returns Value for that data field.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const hero = await reactElements.getResolvedData({
   *   dataItemKey: 'heroImage',
   * });
   * ```
   */
  async getResolvedData({ dataItemKey }: { dataItemKey: string }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getResolvedData(
      manifestContextId,
      this.childPath,
      {
        dataItemKey,
      },
    );
  }

  /**
   * Updates data values for the component. Only the specified data items
   * are updated; other data items remain unchanged.
   *
   * @typeParam T - Optional type parameter to define the expected structure of the input data.
   * @param values - Data item keys and their new values.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * await reactElements.setData({ title: 'New Title', showButton: false });
   * ```
   */
  async setData<T extends ElementData = ElementData>(
    values: ElementDataInput<T>,
  ): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.setData(
      manifestContextId,
      this.childPath,
      values as ElementDataInput,
    );
  }

  /**
   * Retrieves the preset definitions declared in the component's manifest.
   *
   * @returns An object mapping each preset key to its definition.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const presets = await reactElements.getPresetDefinitions();
   * ```
   */
  async getPresetDefinitions(): Promise<ElementPresetDefinitions> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getPresetDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * Retrieves the currently applied preset for the component.
   *
   * @returns Key of the applied preset, or `null` if no preset is applied.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const preset = await reactElements.getAppliedPreset();
   * ```
   */
  async getAppliedPreset() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getAppliedPreset(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * Applies a preset to the component. Applying a preset updates the component's
   * styles and data to match the preset's defined defaults.
   *
   * @param options - Object containing the `key` of the preset to apply.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * await reactElements.applyPreset({ key: 'minimal' });
   * ```
   */
  async applyPreset(options: { key: string }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.applyPreset(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  /**
   * Retrieves the display group definitions declared in the component's manifest.
   *
   * @param options - Optional filter by `groupType`.
   * @returns Display group definitions for the component.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const groups = await reactElements.getDisplayGroupDefinitions();
   *
   * const cssGroups = await reactElements.getDisplayGroupDefinitions({
   *   filter: { groupType: 'cssDataType' },
   * });
   * ```
   */
  // TODO: group type should come from builder enum
  async getDisplayGroupDefinitions(options?: {
    filter?: { groupType?: string };
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getDisplayGroupDefinitions(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  /**
   * Retrieves the display name of the component as defined in the manifest.
   *
   * @returns Component display name.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const name = await reactElements.getDisplayName();
   * ```
   */
  async getDisplayName() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getDisplayName(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * Retrieves the active state for the component.
   *
   * @returns Key of the active state, or `null` if the default state is active.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const state = await reactElements.getState();
   * ```
   */
  async getState() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getState(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * Sets the active state for the component. Pass `null` to return to the
   * default state.
   *
   * @param stateKey - State key to activate, or `null` for the default state.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * await reactElements.setState('hover');
   *
   * await reactElements.setState(null);
   * ```
   */
  async setState(stateKey: string | null) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.setState(
      manifestContextId,
      this.childPath,
      stateKey,
    );
  }

  /**
   * Retrieves the state definitions declared in the component's manifest.
   *
   * @returns An object mapping each state key to its definition.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const states = await reactElements.getStateDefinitions();
   * ```
   */
  async getStateDefinitions(): Promise<ElementStateDefinitions> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getStateDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  // TODO: use type from the ManifestChangeDetection,
  // but type should be moved to editor-platform-public-interfaces
  /**
   * Subscribes to changes on the component. The callback is invoked whenever
   * styles, data, state, preset, props, or manifest values change.
   *
   * @param callback - Callback that receives a patch with `type` and `value`.
   * @returns Unsubscribe method.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const unsubscribe = await reactElements.onChange((patch) => {
   *   if (patch.type === 'data') {
   *     console.log('Data changed:', patch.value);
   *   }
   * });
   *
   * unsubscribe();
   * ```
   */
  async onChange(
    callback: (patch: {
      type:
        | 'styles'
        | 'data'
        | 'state'
        | 'preset'
        | 'props'
        | 'manifest'
        | 'bindings'
        | 'providers';
      value: unknown;
    }) => void,
  ): Promise<() => {}> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.onChange(
      manifestContextId,
      this.childPath,
      callback,
    );
  }

  /**
   * Opens the font picker for selecting a font family and weight from a custom panel.
   *
   * Pass `target` and `panelOptions` to position the popup next to the control that opened it.
   * Use `onChange` / `onPreview` to handle the selection; the editor does not write to the manifest for you.
   *
   * @param params - Picker options.
   * @returns Selected font family and weight, or `undefined` if dismissed.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const font = await reactElements.selectFont({
   *   selectedFontFamily: { family: 'Arial', weight: '400' },
   *   target: buttonRef.current,
   *   onChange: (value) => console.log('Selected:', value),
   * });
   * ```
   */
  async selectFont(params: SelectFontParams) {
    try {
      const privateAPI = await this.getPlatformPrivateAPI();
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedFont = await privateAPI.builderComponent.selectFont(
        manifestContextId,
        this.childPath,
        {
          ...params,
          showWeights: true,
        },
      );
      return selectedFont;
    } catch (error) {
      // TODO: handle error https://wix.atlassian.net/browse/EP-6428
    }
  }

  /**
   * Opens the font family picker without weight selection, from a custom panel.
   *
   * Same pattern as {@link selectFont}: `target`, `panelOptions`, and callbacks.
   *
   * @param params - Picker options.
   * @returns Selected font family, or `undefined` if dismissed.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const font = await reactElements.selectFontFamily({
   *   selectedFontFamily: { family: 'Helvetica', weight: '400' },
   *   target: buttonRef.current,
   *   onChange: (value) => console.log('Selected:', value.family),
   * });
   * ```
   */
  async selectFontFamily(params: SelectFontFamilyParams) {
    try {
      const privateAPI = await this.getPlatformPrivateAPI();
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedFont = await privateAPI.builderComponent.selectFont(
        manifestContextId,
        this.childPath,
        {
          ...params,
          showWeights: false,
        },
      );
      return selectedFont;
    } catch (error) {
      // TODO: handle error https://wix.atlassian.net/browse/EP-6428
    }
  }

  /**
   * Opens the media picker for selecting images, videos, or other media from a custom panel.
   *
   * @param params - Picker options.
   * @returns Selected media items.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const media = await reactElements.selectMedia({
   *   isMultiSelect: true,
   *   category: 'image',
   * });
   * ```
   */
  async selectMedia(params: SelectMediaParams) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.selectMedia(
      manifestContextId,
      this.childPath,
      params,
    );
  }

  /**
   * Opens the link picker for creating or editing a link from a custom panel.
   *
   * @param params - Picker options.
   * @returns Selected link, or `null`/`undefined` if dismissed.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const link = await reactElements.selectLink({
   *   value: currentLink,
   *   options: { linkTypes: ['ExternalLink', 'PageLink'] },
   * });
   * ```
   */
  async selectLink(
    params: SelectLinkParams,
  ): Promise<ElementLink | null | undefined> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.selectLink(
      manifestContextId,
      this.childPath,
      params,
    );
  }
  /**
   * Opens the WQL sort panel for a sort data item on the current component.
   *
   * Pass `dataItemKey` to let the SDK save the selected rule automatically.
   *
   * @param params - WQL sort picker options.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const sort = await reactElements.selectSort({ dataItemKey: 'productSort' });
   * ```
   */
  async selectSort(params: SelectSortParams): Promise<ElementSort | undefined> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.selectSort(
      manifestContextId,
      this.childPath,
      params,
    );
  }

  /**
   * Opens the WQL filter panel for a filter data item on the current component.
   *
   * Pass `dataItemKey` to let the SDK save the selected rule automatically.
   *
   * @param params - WQL filter picker options.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const filter = await reactElements.selectFilter({ dataItemKey: 'productFilter' });
   * ```
   */
  async selectFilter(
    params: SelectFilterParams,
  ): Promise<ElementFilter | undefined> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.selectFilter(
      manifestContextId,
      this.childPath,
      params,
    );
  }

  /**
   * Opens the color picker for selecting a color from a custom panel.
   *
   * @param params - Picker options.
   * @returns Selected color value, or `undefined` if dismissed.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const color = await reactElements.selectColor({
   *   initialValue: '#ff0000',
   *   mode: ['solid', 'gradient'],
   *   showOpacity: true,
   *   onChange: (color) => console.log('Preview:', color),
   *   onApply: (color) => console.log('Applied:', color),
   * });
   * ```
   */
  async selectColor(params: SelectColorParams) {
    const privateAPI = await this.getPlatformPrivateAPI();
    try {
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedColor = await privateAPI.builderComponent.selectColor(
        manifestContextId,
        this.childPath,
        params,
      );
      return selectedColor;
    } catch (error) {
      // TODO: handle error https://wix.atlassian.net/browse/EP-6428
    }
  }

  /**
   * Opens the background picker for selecting a background fill
   * (color, gradient, or image).
   *
   * @param options - Background picker configuration.
   * @returns Selected background fill value.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const bg = await reactElements.selectBackground({
   *   initialValue: { backgroundColor: '#fff', backgroundImage: '' },
   *   onChange: (value) => console.log('Preview:', value),
   * });
   * ```
   */
  async selectBackground(options: BackgroundPickerOptions) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.selectBackground(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  /**
   * Opens the font weight picker for a specified font family, from a custom panel.
   *
   * @param params - Picker options.
   * @returns Selected font weight.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const weight = await reactElements.selectFontWeight({
   *   fontFamily: { family: 'Arial', weight: '400' },
   *   target: buttonRef.current,
   *   onChange: (value) => console.log('Selected weight:', value.weight),
   * });
   * ```
   */
  async selectFontWeight(params: SelectFontWeightParams): Promise<IFontWeight> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return {
      weight: await privateAPI.builderComponent.selectFontWeight(
        manifestContextId,
        this.childPath,
        params,
      ),
    };
  }

  /**
   * Opens the text theme picker from a custom panel — a site theme combination of font and color.
   *
   * @param params - Picker options. Pass `target` for the element in your panel that opened the picker.
   * @param params.initialValue - Initial font and color values.
   * @returns Selected text theme.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const theme = await reactElements.selectTextTheme({
   *   target: buttonRef.current,
   *   initialValue: { font: 'Heading 1', color: '#333' },
   * });
   * ```
   */
  async selectTextTheme(params: SelectTextThemeParams) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.selectTextTheme(
      manifestContextId,
      params,
    );
  }

  /**
   * Retrieves the currently selected index in an array items data group.
   *
   * @param options - Specify `arrayItemsDisplayGroupKey` if the component
   *   has multiple array item groups.
   * @returns Index of the currently selected array item.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const index = await reactElements.getArrayItemsSelectedIndex();
   * ```
   */
  async getArrayItemsSelectedIndex(options?: {
    arrayItemsDisplayGroupKey?: string;
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getArrayItemsSelectedIndex(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  /**
   * Sets the selected index in an array items data group.
   *
   * @param options - Object containing the `index` to select, and optionally
   *   `arrayItemsDisplayGroupKey` if there are multiple array item groups.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * await reactElements.setArrayItemsSelectedIndex({ index: 2 });
   * ```
   */
  async setArrayItemsSelectedIndex(options: {
    index: number;
    arrayItemsDisplayGroupKey?: string;
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.setArrayItemsSelectedIndex(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  /**
   * Resets the selected index in an array items data group to the default.
   *
   * @param options - Specify `arrayItemsDisplayGroupKey` if the component
   *   has multiple array item groups.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * await reactElements.resetArrayItemsSelectedIndex();
   * ```
   */
  async resetArrayItemsSelectedIndex(options?: {
    arrayItemsDisplayGroupKey?: string;
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.resetArrayItemsSelectedIndex(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  /**
   * Retrieves the BI (Business Intelligence) token for the component.
   *
   * @returns BI token string.
   *
   * @example
   * ```ts
   * import { reactElements } from '@wix/editor';
   *
   * const token = await reactElements.getBiToken();
   * ```
   */
  async getBiToken(): Promise<string> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getBiToken(
      manifestContextId,
      this.childPath,
    );
  }
}

export { ElementSDKShape as ReactElementsSDKShape };

export default new PlatformSDKShape(ElementSDKShape)
  .withPublicMethod('getDataDefinitions')
  .withPublicMethod('getData')
  .withPublicMethod('getResolvedData')
  .withPublicMethod('setData')
  .withPublicMethod('getStyleDefinitions')
  .withPublicMethod('getStyles')
  .withPublicMethod('setStyles')
  .withPublicMethod('removeStyles')
  .withPublicMethod('getPresetDefinitions')
  .withPublicMethod('getAppliedPreset')
  .withPublicMethod('applyPreset')
  .withPublicMethod('getDisplayGroupDefinitions')
  .withPublicMethod('getDisplayName')
  .withPublicMethod('getState')
  .withPublicMethod('setState')
  .withPublicMethod('onChange')
  .withPublicMethod('getStateDefinitions')
  .withPublicMethod('selectFont')
  .withPublicMethod('selectFontFamily')
  .withPublicMethod('selectMedia')
  .withPublicMethod('selectLink')
  .withPublicMethod('selectSort')
  .withPublicMethod('selectFilter')
  .withPublicMethod('selectColor')
  .withPublicMethod('selectBackground')
  .withPublicMethod('selectFontWeight')
  .withPublicMethod('selectTextTheme')
  .withPublicMethod('getArrayItemsSelectedIndex')
  .withPublicMethod('setArrayItemsSelectedIndex')
  .withPublicMethod('resetArrayItemsSelectedIndex')
  .withPublicMethod('getBiToken')
  .build();
